import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { Observable, finalize, tap } from 'rxjs';
import { HttpMethod } from '../../shared/enums/http-method.enum';
import { Demo } from '../models/demo.model';
import { DemoService } from '../services/demo.service';
import { LoadCustomDemo, LoadDemo, LoadDemos } from './demo.actions';

export interface DemoStateModel {
    loadingSelectedDemo: boolean;
    selectedDemo: Demo | null;
    loadingDemos: boolean;
    demos: Demo[];
    customDemo: string | null;
}

@State<DemoStateModel>({
    name: 'demoState',
    defaults: {
        loadingSelectedDemo: false,
        selectedDemo: null,
        loadingDemos: false,
        demos: [],
        customDemo: null
    }
})
@Injectable()
export class DemoState {
    private demoService = inject(DemoService);

    @Selector()
    static loadingSelectedDemo(state: DemoStateModel): boolean {
        return state.loadingSelectedDemo;
    }

    @Selector()
    static selectedDemo(state: DemoStateModel): Demo | null {
        return state.selectedDemo;
    }

    @Selector()
    static loadingDemos(state: DemoStateModel): boolean {
        return state.loadingDemos;
    }

    @Selector()
    static demos(state: DemoStateModel): Demo[] {
        return state.demos;
    }

    @Action(LoadDemo)
    public loadSelectedDemo(ctx: StateContext<DemoStateModel>, { payload }: LoadDemo): Observable<Demo> {
        ctx.patchState({ loadingSelectedDemo: true });
        return this.demoService.get(payload.id).pipe(
            tap(selectedDemo => ctx.patchState({ selectedDemo })),
            finalize(() => {
                ctx.patchState({ loadingSelectedDemo: false });
            })
        );
    }

    @Action(LoadDemos)
    public loadDemos(ctx: StateContext<DemoStateModel>): Observable<Demo[]> {
        ctx.patchState({ loadingDemos: true });
        return this.demoService.getAll().pipe(
            tap(demos => ctx.patchState({ demos })),
            finalize(() => {
                ctx.patchState({ loadingDemos: false });
            })
        );
    }

    @Action(LoadCustomDemo)
    public loadCustomDemo(ctx: StateContext<DemoStateModel>): Observable<string> {
        return this.demoService.customRequest<string>(HttpMethod.GET, 'custom-demo-endpoint').pipe(
            tap(customDemo => ctx.patchState({ customDemo })),
            finalize(() => {
                ctx.patchState({ loadingDemos: false });
            })
        );
    }
}
